import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  CalendarDays,
  Camera,
  ExternalLink,
  Image as ImageIcon,
  Loader2,
  MapPin,
  Pencil,
  RefreshCw,
  ScanFace,
  Trash2,
  Upload,
  Users,
} from "lucide-react";
import { Card, CardContent, CardHeader } from "components/ui/card";
import { Button } from "components/ui/button";
import PhotoUploader from "components/PhotoUploader";
import { useAuth } from "contexts/AuthContext";
import { useDialog } from "contexts/DialogContext";
import { useI18n } from "i18n";
import api from "lib/api";
import SportCategoryChip from "components/SportCategoryChip";
import {
  PUBLIC_EVENT_STATUSES,
  categoryLabel,
  formatEventDates,
  formatEventPlace,
} from "lib/events";

const TABS = ["photos", "people"];

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { confirm, alert } = useDialog();
  const { t, tf, lang } = useI18n();

  const [event, setEvent] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("photos");
  const [coverBusy, setCoverBusy] = useState(false);
  const coverInput = useRef(null);
  // Arrastar sobre filhos dispara dragenter/dragleave em cascata: o contador
  // evita o destaque piscar ao passar por cima da miniatura ou do botao.
  const [coverDragging, setCoverDragging] = useState(false);
  const coverDragDepth = useRef(0);

  const canManage = !!user?.capabilities?.manage_events;

  const loadEvent = useCallback(async () => {
    try {
      const res = await api.get(`/api/events/${id}/`);
      setEvent(res.data);
    } catch {
      setEvent(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadPhotos = useCallback(async () => {
    try {
      const res = await api.get(`/api/events/${id}/photos/`);
      setPhotos(res.data || []);
    } catch {
      setPhotos([]);
    }
  }, [id]);

  const loadPeople = useCallback(async () => {
    try {
      const res = await api.get(`/api/events/${id}/people/`);
      setPeople(res.data || []);
    } catch {
      setPeople([]);
    }
  }, [id]);

  useEffect(() => {
    if (!canManage) return;
    loadEvent();
    loadPhotos();
    loadPeople();
  }, [canManage, loadEvent, loadPhotos, loadPeople]);

  // Enquanto houver foto na fila do worker, o painel se atualiza sozinho.
  const pending = photos.filter((p) => ["pending", "processing"].includes(p.status));
  useEffect(() => {
    if (!pending.length) return undefined;
    const timer = setInterval(() => {
      loadPhotos();
      loadEvent();
    }, 5000);
    return () => clearInterval(timer);
  }, [pending.length, loadPhotos, loadEvent]);

  if (user && !canManage) {
    return <Navigate to="/app/dashboard" replace />;
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="w-full animate-fade-in">
        <button
          onClick={() => navigate("/app/events")}
          className="mb-4 flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("common.back")}
        </button>
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            {t("event.notFound")}
          </CardContent>
        </Card>
      </div>
    );
  }

  const onUploaded = () => {
    loadPhotos();
    loadEvent();
    loadPeople();
  };

  const removePhoto = async (photo) => {
    let failed = false;
    const confirmed = await confirm({
      title: t("photo.deleteTitle"),
      description: t("photo.deleteBody"),
      variant: "danger",
      confirmLabel: t("common.delete"),
      cancelLabel: t("common.cancel"),
      onConfirm: async () => {
        try {
          await api.delete(`/api/photos/${photo.id}/`);
        } catch (err) {
          failed = true;
          throw err;
        }
      },
    });

    if (confirmed) {
      onUploaded();
      return;
    }
    if (failed) {
      await alert({
        title: t("common.deleteError"),
        description: t("error.generic"),
        variant: "danger",
      });
    }
  };

  // Limpar o evento: apaga TODAS as fotos. Exige digitar a palavra de
  // confirmacao — um clique so nao pode levar o acervo inteiro.
  const clearPhotos = async () => {
    let failed = false;
    const confirmed = await confirm({
      title: t("photo.clearTitle"),
      description: tf("photo.clearBody", { count: photos.length }),
      variant: "danger",
      confirmLabel: t("photo.clearConfirm"),
      cancelLabel: t("common.cancel"),
      requireText: t("photo.clearWord"),
      onConfirm: async () => {
        try {
          await api.delete(`/api/events/${id}/photos/`);
        } catch (err) {
          failed = true;
          throw err;
        }
      },
    });

    if (confirmed) {
      onUploaded();
      return;
    }
    if (failed) {
      await alert({
        title: t("common.deleteError"),
        description: t("error.generic"),
        variant: "danger",
      });
    }
  };

  const reprocessPhoto = async (photo) => {
    try {
      await api.post(`/api/photos/${photo.id}/reprocess/`);
      loadPhotos();
    } catch (err) {
      await alert({
        title: t("photo.reprocessError"),
        description: err.response?.data?.detail || t("error.generic"),
        variant: "danger",
      });
    }
  };

  // ---------------------------------------------------------------- capa
  // Capa vazia = automatica: o worker adota a primeira foto tratada. Escolher
  // ou enviar fixa a capa; remover devolve o evento ao automatico.
  const setCoverFromPhoto = async (photo) => {
    setCoverBusy(true);
    try {
      const res = await api.post(`/api/events/${id}/cover/`, { photo: photo.id });
      setEvent(res.data);
    } catch (err) {
      await alert({
        title: t("event.coverError"),
        description: err.response?.data?.detail || t("error.generic"),
        variant: "danger",
      });
    } finally {
      setCoverBusy(false);
    }
  };

  const uploadCover = async (file) => {
    if (!file) return;
    setCoverBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post(`/api/events/${id}/cover/`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEvent(res.data);
    } catch (err) {
      await alert({
        title: t("event.coverError"),
        description: err.response?.data?.detail || t("error.generic"),
        variant: "danger",
      });
    } finally {
      setCoverBusy(false);
    }
  };

  // Area da capa aceita arquivo solto (drag and drop), alem do botao.
  const isFileDrag = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");

  const coverDropHandlers = {
    onDragEnter: (e) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      coverDragDepth.current += 1;
      setCoverDragging(true);
    },
    onDragOver: (e) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = coverBusy ? "none" : "copy";
    },
    onDragLeave: (e) => {
      if (!isFileDrag(e)) return;
      coverDragDepth.current = Math.max(0, coverDragDepth.current - 1);
      if (coverDragDepth.current === 0) setCoverDragging(false);
    },
    onDrop: async (e) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      coverDragDepth.current = 0;
      setCoverDragging(false);
      if (coverBusy) return;
      const file = Array.from(e.dataTransfer.files || []).find((f) =>
        f.type.startsWith("image/")
      );
      if (!file) {
        await alert({
          title: t("event.coverError"),
          description: t("event.coverDropInvalid"),
          variant: "danger",
        });
        return;
      }
      uploadCover(file);
    },
  };

  const clearCover = async () => {
    const confirmed = await confirm({
      title: t("event.coverRemoveTitle"),
      description: t("event.coverRemoveBody"),
      variant: "warning",
      confirmLabel: t("common.delete"),
      cancelLabel: t("common.cancel"),
      onConfirm: async () => {
        const res = await api.delete(`/api/events/${id}/cover/`);
        setEvent(res.data);
      },
    });
    if (!confirmed) return;
  };

  const place = formatEventPlace(event);

  return (
    <div className="w-full animate-fade-in">
      <button
        onClick={() => navigate("/app/events")}
        className="mb-4 flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("common.back")}
      </button>

      <Card className="mb-6 w-full">
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight">{event.name}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <CalendarDays className="h-4 w-4" />
                  {formatEventDates(event, lang)}
                </span>
                {place && (
                  <span className="flex items-center gap-1.5">
                    <MapPin className="h-4 w-4" />
                    {event.location_name ? `${event.location_name} — ${place}` : place}
                  </span>
                )}
                <span>
                  {event.category === "sports" && event.sport_name
                    ? `${categoryLabel(t, event.category)} · ${event.sport_name}`
                    : categoryLabel(t, event.category)}
                </span>
                <SportCategoryChip
                  chip={event.sport_category_chip}
                  className="px-2 py-0.5"
                />
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium">
                  {t(`event.status.${event.status}`)}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* `unlisted` tambem tem pagina publica — e justamente o link
                  que o operador precisa copiar para compartilhar. */}
              {PUBLIC_EVENT_STATUSES.includes(event.status) && (
                <a
                  href={`/events/${event.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex h-10 items-center gap-2 rounded-md border border-input px-4 text-sm font-medium transition-colors hover:bg-accent"
                >
                  <ExternalLink className="h-4 w-4" />
                  {t("event.viewPublic")}
                </a>
              )}
              <Button variant="outline" onClick={() => navigate(`/app/events/${id}/edit`)}>
                <Pencil className="h-4 w-4 mr-2" />
                {t("common.edit")}
              </Button>
            </div>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <Stat icon={Camera} label={t("event.photos")} value={event.photo_count} />
            <Stat
              icon={Loader2}
              label={t("event.pending")}
              value={event.pending_count || 0}
            />
            <Stat icon={Users} label={t("event.people")} value={event.people_count} />
          </div>
        </CardHeader>
      </Card>

      <Card className="mb-6 w-full">
        <CardHeader>
          <h2 className="text-lg font-semibold">{t("event.coverTitle")}</h2>
          <p className="text-sm text-muted-foreground">{t("event.coverSubtitle")}</p>
        </CardHeader>
        <CardContent>
          <div
            {...coverDropHandlers}
            className={`relative flex flex-wrap items-center gap-4 rounded-lg border-2 border-dashed p-3 transition-colors ${
              coverDragging ? "border-primary bg-primary/5" : "border-transparent"
            }`}
          >
            {coverDragging && (
              <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-background/80 text-sm font-medium text-primary">
                <Upload className="mr-2 h-4 w-4" />
                {t("event.coverDropHere")}
              </div>
            )}
            <button
              type="button"
              onClick={() => coverInput.current?.click()}
              disabled={coverBusy}
              title={t("event.coverUpload")}
              className="h-24 w-32 shrink-0 overflow-hidden rounded-lg border border-border bg-muted transition-opacity hover:opacity-80 disabled:cursor-wait"
            >
              {event.cover_url ? (
                <img
                  src={event.cover_url}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                  <ImageIcon className="h-6 w-6" />
                </div>
              )}
            </button>

            <div className="flex flex-1 flex-wrap items-center gap-2">
              <input
                ref={coverInput}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  uploadCover(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
              <Button
                variant="outline"
                loading={coverBusy}
                onClick={() => coverInput.current?.click()}
              >
                <Upload className="h-4 w-4 mr-2" />
                {t("event.coverUpload")}
              </Button>
              {event.cover_url && (
                <Button variant="ghost" onClick={clearCover} loading={coverBusy}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t("event.coverRemove")}
                </Button>
              )}
              <p className="w-full text-xs text-muted-foreground">
                {t("event.coverHint")}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6 w-full">
        <CardHeader>
          <h2 className="text-lg font-semibold">{t("photo.uploadTitle")}</h2>
          <p className="text-sm text-muted-foreground">{t("photo.uploadSubtitle")}</p>
        </CardHeader>
        <CardContent>
          <PhotoUploader eventId={id} onUploaded={onUploaded} />
        </CardContent>
      </Card>

      <Card className="w-full">
        <CardHeader>
          <div className="flex items-end gap-1 border-b border-border">
            {TABS.map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setTab(value)}
                className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                  tab === value
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {t(`event.tab.${value}`)}
              </button>
            ))}
            {tab === "photos" && photos.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearPhotos}
                className="mb-1 ml-auto text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-900/20"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t("photo.clear")}
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent>
          {tab === "photos" ? (
            photos.length === 0 ? (
              <EmptyBlock icon={Camera} text={t("photo.empty")} />
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6">
                {photos.map((photo) => (
                  <PhotoTile
                    key={photo.id}
                    photo={photo}
                    t={t}
                    tf={tf}
                    onDelete={removePhoto}
                    onReprocess={reprocessPhoto}
                    onSetCover={setCoverFromPhoto}
                  />
                ))}
              </div>
            )
          ) : people.length === 0 ? (
            <EmptyBlock icon={ScanFace} text={t("event.peopleEmpty")} />
          ) : (
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-5 lg:grid-cols-8">
              {people.map((person) => (
                <div key={person.id} className="text-center">
                  <div className="aspect-square overflow-hidden rounded-full bg-muted">
                    {person.cover_url ? (
                      <img
                        src={person.cover_url}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                        <ScanFace className="h-5 w-5" />
                      </div>
                    )}
                  </div>
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    {tf("event.personPhotos", { count: person.photo_count })}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border p-3">
      <Icon className="h-5 w-5 text-muted-foreground" />
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-lg font-semibold">{value ?? 0}</p>
      </div>
    </div>
  );
}

function PhotoTile({ photo, t, tf, onDelete, onReprocess, onSetCover }) {
  const ready = photo.status === "done";
  const failed = photo.status === "failed";

  return (
    <div className="group relative aspect-square overflow-hidden rounded-lg border border-border bg-muted">
      {ready && photo.thumbnail_url ? (
        <img
          src={photo.thumbnail_url}
          alt={photo.original_name || ""}
          loading="lazy"
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-1 p-2 text-center">
          {failed ? (
            <AlertCircle className="h-5 w-5 text-red-500" />
          ) : (
            <Loader2 className="h-5 w-5 animate-spin text-amber-500" />
          )}
          <span className="text-[10px] text-muted-foreground">
            {t(`photo.status.${failed ? "failed" : "processing"}`)}
          </span>
        </div>
      )}

      {ready && photo.face_count > 0 && (
        <span className="absolute left-1.5 top-1.5 flex items-center gap-1 rounded-full bg-black/70 px-2 py-0.5 text-[10px] font-medium text-white">
          <ScanFace className="h-3 w-3" />
          {photo.face_count}
        </span>
      )}

      <div className="absolute inset-x-0 bottom-0 flex justify-end gap-1 bg-gradient-to-t from-black/70 to-transparent p-1.5 opacity-0 transition-opacity group-hover:opacity-100">
        {ready && (
          <button
            type="button"
            onClick={() => onSetCover(photo)}
            title={t("event.setAsCover")}
            className="rounded bg-white/20 p-1 text-white transition-colors hover:bg-white/30"
          >
            <ImageIcon className="h-3.5 w-3.5" />
          </button>
        )}
        {failed && (
          <button
            type="button"
            onClick={() => onReprocess(photo)}
            title={t("photo.reprocess")}
            className="rounded bg-white/20 p-1 text-white transition-colors hover:bg-white/30"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={() => onDelete(photo)}
          title={t("common.delete")}
          className="rounded bg-white/20 p-1 text-white transition-colors hover:bg-red-500"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {photo.face_status === "unavailable" && (
        <span
          className="absolute right-1.5 top-1.5 rounded-full bg-amber-500/90 px-1.5 py-0.5 text-[10px] font-medium text-white"
          title={tf("photo.faceUnavailable", {})}
        >
          !
        </span>
      )}
    </div>
  );
}

function EmptyBlock({ icon: Icon, text }) {
  return (
    <div className="flex flex-col items-center gap-2 py-12 text-center">
      <Icon className="h-9 w-9 text-muted-foreground/40" />
      <p className="text-sm text-muted-foreground">{text}</p>
    </div>
  );
}
